
import { useEffect, useState, useRef } from 'react';
import { categories } from '../../../mocks/caseStudies';

interface PortfolioHeroProps {
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  totalProjects: number;
}

export default function PortfolioHero({ activeFilter, onFilterChange, totalProjects }: PortfolioHeroProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const tickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const tags = ['Brand Strategy', 'Film Production', 'Outdoor Advertising', 'Digital Campaigns', 'Garment Manufacturing', 'Content Strategy'];

  return (
    <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-10 sm:pb-14 bg-[#0D0D0D] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20moody%20creative%20agency%20workspace%20with%20multiple%20screens%20showing%20brand%20design%20projects%20professional%20photography%20studio%20atmosphere%20dramatic%20low%20key%20lighting%20warm%20amber%20accents%20modern%20creative%20environment%20premium%20commercial%20photography%20cinematic%20wide%20angle&width=1920&height=800&seq=portfolio-hero-bg-001&orientation=landscape"
          alt="Portfolio Background"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/80 via-[#0D0D0D]/90 to-[#0D0D0D]"></div>
      </div>

      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(#038C7F 1px, transparent 1px), linear-gradient(90deg, #038C7F 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`max-w-3xl transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-gallery-line text-[#038C7F] text-sm"></i>
            <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-wider">Portfolio &amp; Case Studies</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[0.9] tracking-tight mb-5">
            OUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">WORK</span>
            <br />
            <span className="text-2xl sm:text-3xl lg:text-4xl font-serif italic text-white/60 leading-relaxed">speaks for itself.</span>
          </h1>

          <p className="text-sm sm:text-base text-gray-400 leading-relaxed max-w-xl mb-8">
            Explore detailed case studies of how we have helped businesses, institutions, and governments across East Africa build credible brands and expand their markets through integrated communication systems.
          </p>

          <div className="flex items-center gap-6 mb-10">
            <div>
              <div className="text-2xl sm:text-3xl font-black text-white">{totalProjects}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Case Studies</div>
            </div>
            <div className="w-px h-10 bg-white/10"></div>
            <div>
              <div className="text-2xl sm:text-3xl font-black text-white">5</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Countries</div>
            </div>
            <div className="w-px h-10 bg-white/10"></div>
            <div>
              <div className="text-2xl sm:text-3xl font-black text-white">100%</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">Client Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className={`transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => onFilterChange(cat)}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === cat
                    ? 'bg-white text-[#0D0D0D] shadow-lg shadow-white/10'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                {cat}
                {activeFilter === cat && <span className="w-1.5 h-1.5 bg-[#038C7F] rounded-full"></span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="relative z-10 mt-10 border-t border-white/5">
        <div className="overflow-hidden py-3">
          <div ref={tickerRef} className="flex animate-ticker whitespace-nowrap">
            {[...tags, ...tags, ...tags].map((tag, idx) => (
              <div key={idx} className="flex items-center gap-4 mx-4 flex-shrink-0">
                <span className="text-xs font-semibold text-white/20 uppercase tracking-wider">{tag}</span>
                <span className="w-1 h-1 rounded-full bg-[#038C7F]/30 flex-shrink-0"></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-ticker {
          animation: ticker 25s linear infinite;
        }
      `}</style>
    </section>
  );
}
